import { parseDbTimestamp } from "@repo/shared";
import { Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSession } from "@/lib/auth-client";
import {
  useTenantOrganization,
  useUpdateTenantOrganization,
} from "@/lib/hooks/use-tenant-organization";
import { useT } from "@/lib/i18n/use-t";
import { hasPermissionType, type Permission } from "@/lib/permissions";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { getRootDomain } from "@/lib/runtime-config";
import { ChangeSlugDialog } from "./change-slug-dialog";
import { ClientAccessCard } from "./client-access";
import { T } from "./organization-settings.i18n";

/** `<slug>.<root>` with tenant subdomains, otherwise the bare slug (the organization identifier). */
export function tenantAddress(slug: string, rootDomain: string | null): string {
  return rootDomain ? `${slug}.${rootDomain}` : slug;
}

/** QL-358 — Settings → Organization: rename the organization and change its address. */
export function OrganizationSettingsWrapper() {
  const t = useT(T);
  const { formatDate } = useRegionalFormat();
  const { data: sessionData, isPending: sessionLoading } = useSession();
  const userPermissions = ((sessionData?.user as { permissions?: string[] } | undefined)
    ?.permissions ?? ["advisor"]) as Permission[];
  const isAdmin = hasPermissionType(userPermissions, "admin");

  const { organization, isLoading } = useTenantOrganization();
  const updateOrganization = useUpdateTenantOrganization();
  const [name, setName] = useState<string | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const rootDomain = getRootDomain();

  const header = (
    <PageHeader
      breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.organization }]}
      title={t.organization}
      description={t.description}
    />
  );

  if (sessionLoading) return null;
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        {header}
        <p className="text-sm text-muted-foreground">{t.noAccess}</p>
      </div>
    );
  }
  if (isLoading || !organization) return null;

  const currentName = name ?? organization.name;
  const nameChanged = name !== null && name.trim() !== organization.name;
  const address = tenantAddress(organization.slug, rootDomain);

  const handleSaveName = () => {
    updateOrganization.mutate(
      { name: currentName.trim() },
      {
        onSuccess: () => {
          toast.success(t.nameSaved);
          setName(null);
        },
        onError: (err: unknown) =>
          toast.error(t.error, {
            description: err instanceof Error && err.message ? err.message : t.failedToSave,
          }),
      },
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rootDomain ? `https://${address}` : address);
      toast.success(t.copied);
    } catch {
      // Clipboard unavailable (permissions, insecure context): the address stays visible
    }
  };

  return (
    <div className="space-y-6">
      {header}

      {!organization.canEdit && <p className="text-sm text-muted-foreground">{t.notEditable}</p>}

      <Card>
        <CardHeader>
          <CardTitle>{t.name}</CardTitle>
          <CardDescription>{t.nameDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="organization-name" className="sr-only">
                {t.name}
              </Label>
              <Input
                id="organization-name"
                value={currentName}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                disabled={!organization.canEdit || updateOrganization.isPending}
              />
            </div>
            <Button
              onClick={handleSaveName}
              disabled={!organization.canEdit || !nameChanged || currentName.trim() === ""}
              loading={updateOrganization.isPending}
            >
              {t.save}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.address}</CardTitle>
          <CardDescription>{t.addressDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
              <span className="flex-1 truncate font-mono text-sm">{address}</span>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={handleCopy}
                tooltip={t.copy}
              >
                <Copy className="size-4" aria-hidden="true" />
              </Button>
            </div>
            <Button
              variant="outline"
              onClick={() => setChangeOpen(true)}
              disabled={!organization.canEdit || organization.slugChangeAvailableAt !== null}
            >
              {t.changeAddress}
            </Button>
          </div>

          {organization.slugChangeAvailableAt && (
            <p className="text-sm text-muted-foreground">
              {t.cooldown(formatDate(organization.slugChangeAvailableAt))}
            </p>
          )}

          {organization.reservedSlugs.length > 0 && (
            <div className="space-y-1 text-sm">
              <p className="font-medium">{t.reservedTitle}</p>
              <ul className="list-disc pl-5 text-muted-foreground">
                {organization.reservedSlugs.map((reserved) => (
                  <li key={reserved.slug}>
                    {t.reservedItem(
                      tenantAddress(reserved.slug, rootDomain),
                      formatDate(parseDbTimestamp(reserved.reservedUntil)),
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <ClientAccessCard />

      <ChangeSlugDialog
        open={changeOpen}
        onOpenChange={setChangeOpen}
        currentSlug={organization.slug}
        rootDomain={rootDomain}
      />
    </div>
  );
}
